import { endAndReportPerformanceMeasure, startPerformanceMeasure } from '@ms/yammer-telemetry';
import type { PerformanceEventProperties, PerformanceMeasure } from '@ms/yammer-telemetry-support';

import { clearScenarioCallLedger, readScenarioCallLedger } from './scenarioCallLedger';
import { composeScenarioWireValue } from './scenarioWireValue';
import type { ScenarioId, ScenarioName, ScenarioSource, ScenarioStatus } from './types';

const fiveMinutesInMS = 300_000;

interface ScenarioHandle {
  readonly measure: PerformanceMeasure;
  readonly name: ScenarioName;
  readonly source?: ScenarioSource;
  readonly timer: number;
}

const scenarios = new Map<ScenarioId, ScenarioHandle>();

interface ReliabilityFlags {
  readonly endedWithError: boolean;
  readonly timedOut: boolean;
  readonly interrupted: boolean;
}

/**
 * Maps the richer scenario status onto the primitive's boolean reliability flags so today's
 * reliability metric keeps working. The full status value always rides along in `scenarioStatus`.
 */
const statusToReliabilityFlags: Record<ScenarioStatus, ReliabilityFlags> = {
  ok: { endedWithError: false, timedOut: false, interrupted: false },
  failure: { endedWithError: true, timedOut: false, interrupted: false },
  timeout: { endedWithError: false, timedOut: true, interrupted: false },
  cancel: { endedWithError: false, timedOut: false, interrupted: true },
  incomplete: { endedWithError: false, timedOut: false, interrupted: true },
  ignore: { endedWithError: false, timedOut: false, interrupted: false },
};

export interface LogScenarioStopOptions {
  readonly scenarioId: ScenarioId;
  readonly status: ScenarioStatus;
  readonly error?: Error;
}

/**
 * Ends a scenario and reports it. `status` drives the reliability flags and is attached as a
 * property alongside the composed `scenario` wire value, `source` and scenario id. The
 * emitted `scenario` is the composed wire value (see `composeScenarioWireValue`): mobile's
 * exact token for a mapped `(name, source)` pair so web and mobile join on it directly, or
 * the web-native `name` for unmapped interaction scenarios (reconciled query-side instead). A
 * `failure` status may carry an `error` whose name/message become `errorCode`/`errorMessage`.
 * On any non-ok outcome, the scenario's traceability call-ledger (if any calls were recorded)
 * is serialized onto the `scenarioCallLedger` property so a failed/timed-out scenario can be
 * decomposed into the backend calls that composed it; ok scenarios omit it (cardinality/PII).
 * The ledger is always cleared here so ids can't leak past the scenario's span.
 * Unknown or already-stopped scenario ids are a no-op.
 */
export const logScenarioStop = ({ scenarioId, status, error }: LogScenarioStopOptions): void => {
  const handle = scenarios.get(scenarioId);

  if (!handle) {
    return;
  }

  window.clearTimeout(handle.timer);
  scenarios.delete(scenarioId);

  const { measure, name, source } = handle;

  const ledgerRows = readScenarioCallLedger(scenarioId);
  clearScenarioCallLedger(scenarioId);

  const eventProperties: PerformanceEventProperties = {
    ...measure.eventProperties,
    ...statusToReliabilityFlags[status],
    scenario: composeScenarioWireValue(name, source),
    scenarioStatus: status,
    scenarioId,
    ...(source ? { source } : {}),
    ...(error ? { errorCode: error.name, errorMessage: error.message } : {}),
    ...(status !== 'ok' && ledgerRows.length > 0 ? { scenarioCallLedger: JSON.stringify(ledgerRows) } : {}),
  };

  endAndReportPerformanceMeasure({ ...measure, eventProperties });
};

export interface LogScenarioStartOptions {
  readonly name: ScenarioName;
  readonly source?: ScenarioSource;
  /**
   * How long to wait before the scenario auto-reports with a `timeout` status.
   * @default 300_000 (5 minutes)
   */
  readonly timeoutInMS?: number;
}

/**
 * Starts a scenario measure and returns an opaque scenario id. No event is emitted on
 * start (mirrors mobile). If `logScenarioStop` is not called within `timeoutInMS`, the
 * scenario auto-reports with a `timeout` status so orphaned scenarios never leak.
 */
export const logScenarioStart = ({ name, source, timeoutInMS }: LogScenarioStartOptions): ScenarioId => {
  const scenarioId = crypto.randomUUID() as ScenarioId;
  const measure = startPerformanceMeasure('multi_step_scenario', name);

  const timer = window.setTimeout(() => {
    logScenarioStop({ scenarioId, status: 'timeout' });
  }, timeoutInMS ?? fiveMinutesInMS);

  scenarios.set(scenarioId, { measure, name, source, timer });

  return scenarioId;
};
